import classNames from 'classnames';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import {
  requestVideoPlayerEnterFullScreen,
  requestVideoPlayerLeaveFullScreen,
  requestVideoPlayerMute,
  requestVideoPlayerPause,
  requestVideoPlayerPlay,
  requestVideoPlayerPlaybackRate,
  requestVideoPlayerUnmute,
  requestVideoPlayerVolume,
  updateTheaterMode,
} from 'actions/videoPlayer';
import { SMALL_TABLET_MIN_WIDTH } from 'constants/breakpoints';
import { NERVOUS } from 'constants/emojiReactions';
import { FULL_SCREEN_CLICK, THEATER_MODE_TOGGLED } from 'constants/events';
import { ROLLOUT_AT_MENTION_COMMENT } from 'constants/featureFlag';
import { selectCommentsOrEmojiEnabled } from 'selectors/video';
import * as analytics from 'utilities/analytics';
import { isElectron } from 'utilities/device';

import AnonymousNameInput from './anonymous-name-input';
import CcButton from './cc-button';
import CompactOptionsButton from './compact-options-button';
import EmbedReactionBar from './embed-reaction-bar';
import FullscreenButton from './fullscreen-button';
import PipButton from './pip-button';
import PlayPauseButton from './play-pause-button';
import PlaybackRateControl from './playback-rate-control';
import SeekBar from './seek-bar';
import StepBackFiveButton from './step-back-five-button';
import StepForwardFiveButton from './step-forward-five-button';
import TextToSpeechButton from './text-to-speech-button';
import TheaterModeButton from './theaterModeButton';
import TimeDisplay from './time-display';
import VolumeControl from './volume-control';
import WatchAgainButton from './watch-again-button';
import CommentBar from '../comment-bar';
import EmojiBar from '../emoji-bar';
import { EmbedContext } from '../../embed-video';
import {
  selectControlBarExpanded,
  selectEmojiOrCommentOnBar,
  selectShowEmojiBarInsidePlayer,
} from '../../../selectors/videoPlayer';
import { KEY_THEATER_MODE } from '../../../../../shared/constants/cookie';

import { PrefetchFlags } from 'components/feature-flag-wrapper';
import MediaQuery from 'components/media-query';
import {
  NewWaveformWrapper,
  OldWaveformWrapper,
} from 'components/share-video/edit-tools-old/waveform-flag-wrapper';

import './styles.less';

class ControlBar extends Component {
  render() {
    const {
      controlBarExpanded,
      currentTime,
      currentUserIsOwner,
      displayedDuration,
      duration,
      ended,
      fullScreen,
      inTrimMode,
      inCommentMode,
      inAnonymousReactionMode,
      isMainVideoOnSharePage,
      muted,
      paused,
      playbackRate,
      seeking,
      showEmojiBarInsidePlayer,
      theaterMode,
      timeOffset,
      emojiOrCommentOnBar,
      volume,
      volumeControlActive,
      pipSupported,
      togglePictureInPicture,
      canShowCaptions,
      captionsVisible,
      setCaptionsVisible,
      rolloutNewEndScreen,
      commentsOrEmojiEnabled,
      hasStarted,
    } = this.props;

    let fullScreenButton = null;
    let playbackRateControl = null;

    const showPipButton = pipSupported && !isElectron;

    fullScreenButton = (
      <>
        <NewWaveformWrapper>
          <FullscreenButton
            fullScreen={fullScreen}
            seeking={seeking}
            onClick={this.handleToggleFullscreen}
            inTrimMode={inTrimMode}
          />
        </NewWaveformWrapper>

        <OldWaveformWrapper>
          {!inTrimMode && (
            <FullscreenButton
              fullScreen={fullScreen}
              seeking={seeking}
              onClick={this.handleToggleFullscreen}
            />
          )}
        </OldWaveformWrapper>
      </>
    );

    playbackRateControl = (
      <>
        <NewWaveformWrapper>
          <PlaybackRateControl
            playbackRate={playbackRate}
            seeking={seeking}
            onRateChange={this.props.requestVideoPlayerPlaybackRate}
          />
        </NewWaveformWrapper>

        <OldWaveformWrapper>
          {!inTrimMode && (
            <PlaybackRateControl
              playbackRate={playbackRate}
              seeking={seeking}
              onRateChange={this.props.requestVideoPlayerPlaybackRate}
            />
          )}
        </OldWaveformWrapper>
      </>
    );

    const standardBottomControls = (
      <Fragment>
        <div className="left-controls">
          {ended ? (
            <WatchAgainButton />
          ) : (
            <PlayPauseButton
              paused={paused}
              onClick={this.handleTogglePlay}
              seeking={seeking}
              ended={ended}
            />
          )}

          <StepBackFiveButton
            isMainVideoOnSharePage={isMainVideoOnSharePage}
            seeking={seeking}
            crowded={emojiOrCommentOnBar}
          />
          <StepForwardFiveButton
            isMainVideoOnSharePage={isMainVideoOnSharePage}
            seeking={seeking}
            crowded={emojiOrCommentOnBar}
          />
          <VolumeControl
            muted={muted}
            volume={volume}
            seeking={seeking}
            onButtonClick={this.handleToggleMute}
            onVolumeChange={this.props.requestVideoPlayerVolume}
          />
          {showEmojiBarInsidePlayer && (
            <TimeDisplay
              className="next-to-volume"
              currentTime={timeOffset + currentTime}
              duration={displayedDuration ?? duration}
            />
          )}
        </div>
        {!showEmojiBarInsidePlayer && (
          <TimeDisplay
            volumeControlActive={volumeControlActive}
            currentTime={timeOffset + currentTime}
            duration={displayedDuration ?? duration}
          />
        )}
        {showEmojiBarInsidePlayer && (
          <EmojiBar
            hideReaction={NERVOUS}
            drawnInsidePlayer={true}
            controlBarExpanded={controlBarExpanded}
          />
        )}
        <div className="right-controls">
          <MediaQuery query={`(min-width: ${SMALL_TABLET_MIN_WIDTH}px)`}>
            {canShowCaptions && (
              <>
                <NewWaveformWrapper>
                  <CcButton
                    onClick={() => setCaptionsVisible(!captionsVisible)}
                    ccOn={captionsVisible}
                    controlBarExpanded={controlBarExpanded}
                  />
                </NewWaveformWrapper>

                <OldWaveformWrapper>
                  {!inTrimMode && (
                    <CcButton
                      onClick={() => setCaptionsVisible(!captionsVisible)}
                      ccOn={captionsVisible}
                      controlBarExpanded={controlBarExpanded}
                    />
                  )}
                </OldWaveformWrapper>
              </>
            )}

            {playbackRateControl}
          </MediaQuery>

          <MediaQuery query={`(max-width: ${SMALL_TABLET_MIN_WIDTH - 1}px)`}>
            <NewWaveformWrapper>
              <CompactOptionsButton
                controlBarExpanded={controlBarExpanded}
                canShowCaptions={canShowCaptions}
                showCaptions={captionsVisible}
                setShowCaptions={setCaptionsVisible}
              />
            </NewWaveformWrapper>

            <OldWaveformWrapper>
              {!inTrimMode && (
                <CompactOptionsButton
                  controlBarExpanded={controlBarExpanded}
                  canShowCaptions={canShowCaptions}
                  showCaptions={captionsVisible}
                  setShowCaptions={setCaptionsVisible}
                />
              )}
            </OldWaveformWrapper>
          </MediaQuery>

          {isMainVideoOnSharePage && (
            <>
              <NewWaveformWrapper>
                <TheaterModeButton
                  onClick={this.handleTheaterModeClick}
                  theaterMode={theaterMode}
                  inTrimMode={inTrimMode}
                />
              </NewWaveformWrapper>

              <OldWaveformWrapper>
                {!inTrimMode && (
                  <TheaterModeButton
                    onClick={this.handleTheaterModeClick}
                    theaterMode={theaterMode}
                  />
                )}
              </OldWaveformWrapper>
            </>
          )}

          {showPipButton && (
            <>
              <NewWaveformWrapper>
                <PipButton
                  onClick={togglePictureInPicture}
                  seeking={seeking}
                  crowded={emojiOrCommentOnBar}
                  inTrimMode={inTrimMode}
                />
              </NewWaveformWrapper>

              <OldWaveformWrapper>
                {!inTrimMode && (
                  <PipButton
                    onClick={togglePictureInPicture}
                    seeking={seeking}
                    crowded={emojiOrCommentOnBar}
                  />
                )}
              </OldWaveformWrapper>
            </>
          )}

          <>
            <NewWaveformWrapper>
              <TextToSpeechButton
                seeking={seeking}
                crowded={emojiOrCommentOnBar}
                inTrimMode={inTrimMode}
              />
            </NewWaveformWrapper>

            <OldWaveformWrapper>
              {!inTrimMode && (
                <TextToSpeechButton
                  seeking={seeking}
                  crowded={emojiOrCommentOnBar}
                />
              )}
            </OldWaveformWrapper>
          </>

          {fullScreenButton}
        </div>
      </Fragment>
    );

    function controlBarContent() {
      if (inCommentMode) {
        return <CommentBar />;
      }

      if (inAnonymousReactionMode) {
        return <AnonymousNameInput />;
      }

      if (
        rolloutNewEndScreen &&
        ended &&
        !isMainVideoOnSharePage &&
        commentsOrEmojiEnabled
      ) {
        return <EmbedReactionBar />;
      }

      return standardBottomControls;
    }

    const showPrePlaySeekBar = !hasStarted && currentTime > 0;

    return (
      <EmbedContext.Consumer>
        {({ hideControlBar }) =>
          !hideControlBar && (
            <div className="video-player-control-bar-container">
              <div
                className={classNames(
                  'control-bar',
                  controlBarExpanded && 'expanded'
                )}
                ref={controlBar => (this.controlBar = controlBar)}
              >
                {hasStarted &&
                  (inTrimMode ? (
                    <OldWaveformWrapper>
                      <SeekBar
                        controlBarExpanded={controlBarExpanded}
                        currentTime={currentTime}
                        currentUserIsOwner={currentUserIsOwner}
                        duration={duration}
                        isMainVideoOnSharePage={isMainVideoOnSharePage}
                        fullScreen={fullScreen}
                        paused={paused}
                        onPause={this.props.requestVideoPlayerPause}
                        onPlay={playAfterSeek =>
                          this.props.requestVideoPlayerPlay(playAfterSeek)
                        }
                        containerDimensions={this.props.containerDimensions}
                      />
                    </OldWaveformWrapper>
                  ) : (
                    <SeekBar
                      controlBarExpanded={controlBarExpanded}
                      currentTime={currentTime}
                      currentUserIsOwner={currentUserIsOwner}
                      duration={duration}
                      isMainVideoOnSharePage={isMainVideoOnSharePage}
                      fullScreen={fullScreen}
                      paused={paused}
                      onPause={this.props.requestVideoPlayerPause}
                      onPlay={playAfterSeek =>
                        this.props.requestVideoPlayerPlay(playAfterSeek)
                      }
                      containerDimensions={this.props.containerDimensions}
                    />
                  ))}
                {showPrePlaySeekBar && (
                  <SeekBar
                    controlBarExpanded={controlBarExpanded}
                    currentTime={currentTime}
                    currentUserIsOwner={currentUserIsOwner}
                    duration={duration}
                    isMainVideoOnSharePage={isMainVideoOnSharePage}
                    fullScreen={fullScreen}
                    paused={paused}
                    onPause={this.props.requestVideoPlayerPause}
                    onPlay={playAfterSeek =>
                      this.props.requestVideoPlayerPlay(playAfterSeek)
                    }
                    containerDimensions={this.props.containerDimensions}
                  />
                )}

                <div
                  className={classNames('bottom-controls', {
                    'in-comment-mode': inCommentMode,
                  })}
                >
                  {/* Prefetch to prevent comment input lost focus */}
                  <PrefetchFlags flags={[ROLLOUT_AT_MENTION_COMMENT]} />
                  {controlBarContent()}
                </div>
              </div>
            </div>
          )
        }
      </EmbedContext.Consumer>
    );
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  getControlBarEl() {
    return this.controlBar;
  }

  handleTogglePlay = () => {
    if (this.props.paused) {
      this.props.requestVideoPlayerPlay();
    } else {
      this.props.requestVideoPlayerPause();
    }
  };

  handleTheaterModeClick = () => {
    const { currentUserIsOwner, theaterMode, inTrimMode } = this.props;
    const newTheaterModeVal = inTrimMode ? false : !theaterMode;

    document.cookie = `${KEY_THEATER_MODE} = ${newTheaterModeVal};`;

    this.props.updateTheaterMode(newTheaterModeVal, inTrimMode);
    analytics.track(THEATER_MODE_TOGGLED, {
      currentUserIsOwner,
      on: newTheaterModeVal,
    });
  };

  handleToggleFullscreen = () => {
    if (this.props.fullScreen) {
      this.props.requestVideoPlayerLeaveFullScreen();
    } else {
      this.props.requestVideoPlayerEnterFullScreen();
    }

    analytics.track(FULL_SCREEN_CLICK, {
      from_double_click: false,
      full_screen: this.props.fullScreen,
    });
  };

  handleToggleMute = () => {
    if (this.props.muted) {
      this.props.requestVideoPlayerUnmute();
    } else {
      this.props.requestVideoPlayerMute();
    }
  };
}
const mapStateToProps = state => {
  const {
    current_time: currentTime,
    displayed_duration: displayedDuration,
    duration,
    full_screen: fullScreen,
    isMainVideoOnSharePage,
    muted,
    paused,
    playback_rate: playbackRate,
    seeking,
    theater_mode: theaterMode,
    time_offset: timeOffset,
    volume_control_active: volumeControlActive,
    video,
    volume,
    pipSupported,
    in_comment_mode: inCommentMode,
    in_anonymous_reaction_mode: inAnonymousReactionMode,
    has_started: hasStarted,
  } = state.video_player;

  const { in_trim_mode: inTrimMode } = state.trimming;

  return {
    controlBarExpanded: selectControlBarExpanded(state),
    currentTime,
    currentUserIsOwner: video.current_user_is_owner,
    displayedDuration,
    duration,
    fullScreen,
    inTrimMode,
    inCommentMode,
    inAnonymousReactionMode,
    isMainVideoOnSharePage,
    muted,
    paused,
    playbackRate,
    seeking,
    theaterMode,
    timeOffset,
    emojiOrCommentOnBar: selectEmojiOrCommentOnBar(state),
    showEmojiBarInsidePlayer: selectShowEmojiBarInsidePlayer(state),
    volume,
    volumeControlActive,
    pipSupported,
    videoId: video.id,
    commentsOrEmojiEnabled: selectCommentsOrEmojiEnabled(state),
    hasStarted: hasStarted || inTrimMode,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateTheaterMode: (theaterMode, inTrimMode) =>
      dispatch(updateTheaterMode(theaterMode, inTrimMode)),
    requestVideoPlayerEnterFullScreen: () =>
      dispatch(requestVideoPlayerEnterFullScreen()),
    requestVideoPlayerLeaveFullScreen: () =>
      dispatch(requestVideoPlayerLeaveFullScreen()),
    requestVideoPlayerMute: () => dispatch(requestVideoPlayerMute()),
    requestVideoPlayerPause: () => dispatch(requestVideoPlayerPause()),
    requestVideoPlayerPlay: playAfterSeek =>
      dispatch(requestVideoPlayerPlay(playAfterSeek)),
    requestVideoPlayerPlaybackRate: playbackRate =>
      dispatch(requestVideoPlayerPlaybackRate(playbackRate)),
    requestVideoPlayerVolume: volume =>
      dispatch(requestVideoPlayerVolume(volume)),
    requestVideoPlayerUnmute: () => dispatch(requestVideoPlayerUnmute()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(ControlBar);
